import { useCallback } from 'react';
import L from 'leaflet';

// Создаем пользовательские иконки для начала и конца трека (копия из TrackMap)
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

const startIcon = new L.Icon({
  iconUrl: iconUrl,
  iconRetinaUrl: iconRetinaUrl,
  shadowUrl: shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'track-start-marker'
});

const endIcon = new L.Icon({
  iconUrl: iconUrl,
  iconRetinaUrl: iconRetinaUrl,
  shadowUrl: shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'track-end-marker'
});

export const useMapVisualization = (mapRefs, formatDate, formatTime, handleMapClick) => {
  const { mapInstanceRef, markersRef, routePathRef, animatedMarkerRef, highlightedPathRef } = mapRefs;
  
  // Внутренняя функция обновления карты с данными трека (точная копия из TrackMap)
  const updateMapWithTrackDataInternal = useCallback((data, currentVehicle, handleMapClick) => {
    if (!mapInstanceRef.current || !data || data.length === 0) {
      console.warn('TrackReport: Нет экземпляра карты или данных для обновления');
      return false;
    }
    
    try {
      const map = mapInstanceRef.current;
      
      // Проверяем, что карта все еще существует в DOM
      if (!map._container || !document.body.contains(map._container)) {
        console.warn('TrackReport: Контейнер карты был удален из DOM, требуется реинициализация');
        // Очищаем ссылку на несуществующий экземпляр
        mapInstanceRef.current = null;
        return false; // Сигнализируем о необходимости реинициализации
      }
      
      // Очистка предыдущих маркеров
      if (markersRef.current.length > 0) {
        markersRef.current.forEach(marker => {
          if (map) {
            map.removeLayer(marker);
          }
        });
        markersRef.current = [];
      }
      
      // Очистка предыдущего пути
      if (routePathRef.current) {
        map.removeLayer(routePathRef.current);
        routePathRef.current = null;
      }
      
      // Очистка анимированного маркера
      if (animatedMarkerRef.current) {
        map.removeLayer(animatedMarkerRef.current);
        animatedMarkerRef.current = null;
      }
      
      // Очистка выделенного сегмента
      if (highlightedPathRef.current) {
        map.removeLayer(highlightedPathRef.current);
        highlightedPathRef.current = null;
      }
      
      // Создаем массив точек для линии маршрута
      const routePoints = data.map(point => [point.latitude, point.longitude]);
      
      // Создаем линию маршрута
      const routePath = L.polyline(routePoints, {
        color: '#4e73df',
        weight: 4,
        opacity: 0.8,
        lineJoin: 'round'
      });
      
      // Добавляем обработчик события клика на линию трека
      routePath.on('click', handleMapClick);
      
      // Добавляем линию на карту
      routePath.addTo(map);
      routePathRef.current = routePath;
      
      // Добавляем маркеры начала и конца трека
      if (data.length > 0) {
        // Маркер начала трека
        const startPoint = data[0];
        const startMarker = L.marker([startPoint.latitude, startPoint.longitude], { icon: startIcon })
          .addTo(map)
          .bindPopup(`<strong>Начало трека</strong><br>
                     Дата: ${formatDate(startPoint.timestamp)}<br>
                     Скорость: ${startPoint.speed} км/ч`)
          .on('click', () => {
            // Отправляем событие выбора первой точки
            const pointSelectedEvent = new CustomEvent('pointSelected', {
              detail: {
                timestamp: new Date(startPoint.timestamp).getTime(),
                data: startPoint,
                source: 'trackReport'
              }
            });
            document.dispatchEvent(pointSelectedEvent);
          });
        
        // Маркер конца трека
        const endPoint = data[data.length - 1];
        const endMarker = L.marker([endPoint.latitude, endPoint.longitude], { icon: endIcon })
          .addTo(map)
          .bindPopup(`<strong>Конец трека</strong><br>
                     Дата: ${formatDate(endPoint.timestamp)}<br>
                     Скорость: ${endPoint.speed} км/ч`)
          .on('click', () => {
            // Отправляем событие выбора последней точки
            const pointSelectedEvent = new CustomEvent('pointSelected', {
              detail: {
                timestamp: new Date(endPoint.timestamp).getTime(),
                data: endPoint,
                source: 'trackReport'
              }
            });
            document.dispatchEvent(pointSelectedEvent);
          });
        
        // Добавляем маркеры в массив для возможности дальнейшего управления
        markersRef.current.push(startMarker, endMarker);
        
        // Создаем анимированный маркер для воспроизведения трека
        const animatedMarker = L.marker([startPoint.latitude, startPoint.longitude])
          .addTo(map)
          .bindPopup(`<strong>${currentVehicle?.name || 'Транспорт'}</strong><br>
                     Дата: ${formatDate(startPoint.timestamp)}<br>
                     Скорость: ${startPoint.speed} км/ч`);
        
        animatedMarkerRef.current = animatedMarker;
      }
      
      // Добавляем общий обработчик клика для карты
      map.off('click', handleMapClick); // Удаляем предыдущий обработчик, если есть
      map.on('click', handleMapClick);
      
      console.log('TrackReport: Карта обновлена с данными трека');
      return true;
    } catch (error) {
      console.error('TrackReport: Ошибка при обновлении карты:', error);
      return false;
    }
  }, [mapInstanceRef, markersRef, routePathRef, animatedMarkerRef, highlightedPathRef, formatDate, handleMapClick]);

  // Обновление представления карты с защитой от ошибок (точная копия из TrackMap)
  const updateMapView = useCallback((trackData) => {
    if (!mapInstanceRef.current || !trackData?.length) {
      if (!mapInstanceRef.current) {
        console.warn('TrackReport: Экземпляр карты не существует при обновлении представления');
      }
      if (!trackData?.length) {
        console.warn('TrackReport: Нет данных трека для обновления представления');
      }
      return false;
    }
    
    try {
      const map = mapInstanceRef.current;
      
      // Проверяем, что карта все еще находится в DOM
      if (!map._container || !document.body.contains(map._container)) {
        console.warn('TrackReport: Контейнер карты был удален из DOM, требуется реинициализация');
        // Очищаем ссылку на несуществующий экземпляр
        mapInstanceRef.current = null;
        return false; // Сигнализируем о необходимости реинициализации
      }
      
      // Создаем границы на основе всех точек трека
      const allCoords = trackData.map(point => [point.latitude, point.longitude]);
      const bounds = L.latLngBounds(allCoords);
      
      // Проверяем корректность границ
      if (bounds.isValid()) {
        map.fitBounds(bounds, {
          padding: [50, 50], // Отступы от краев
          maxZoom: 15, // Максимальный зум
          animate: true, // Анимированное перемещение
          duration: 0.5 // Длительность анимации
        });
        
        // Принудительно обновляем размеры карты после изменения границ
        setTimeout(() => {
          if (map && map._container && document.body.contains(map._container)) {
            map.invalidateSize();
          }
        }, 100);
      } else {
        console.warn('TrackReport: Некорректные границы трека для отображения');
      }
      return true;
    } catch (error) {
      console.error('TrackReport: Ошибка при обновлении представления карты:', error);
      return false;
    }
  }, [mapInstanceRef]);

  // Функция обновления размеров карты
  const refreshMapView = useCallback(() => {
    if (!mapInstanceRef.current) {
      console.warn('TrackReport: Экземпляр карты не существует при обновлении размеров');
      return false;
    }
    
    try {
      const map = mapInstanceRef.current;
      
      // Проверяем, что карта все еще находится в DOM
      if (!map._container || !document.body.contains(map._container)) {
        console.warn('TrackReport: Контейнер карты был удален из DOM, требуется реинициализация');
        // Очищаем ссылку на несуществующий экземпляр
        mapInstanceRef.current = null;
        return false; // Сигнализируем о необходимости реинициализации
      }
      
      // Принудительно обновляем размеры карты
      setTimeout(() => {
        if (map && map._container && document.body.contains(map._container)) {
          console.log('TrackReport: Обновление размеров карты');
          map.invalidateSize();
        }
      }, 50);
      return true;
    } catch (error) {
      console.error('TrackReport: Ошибка при обновлении размеров карты:', error);
      return false;
    }
  }, [mapInstanceRef]);

  // Функция обновления анимированного маркера (копия из TrackMap)
  const updateAnimatedMarker = useCallback((index, trackData, currentVehicle) => {
    if (!animatedMarkerRef.current || !trackData || index < 0 || index >= trackData.length) {
      return;
    }
    
    try {
      const point = trackData[index];
      
      // Проверяем наличие координат
      if (!point.latitude || !point.longitude) {
        console.warn('TrackReport: Точка не содержит координат', point);
        return;
      }
      
      // Обновляем позицию маркера
      animatedMarkerRef.current.setLatLng([point.latitude, point.longitude]);
      
      // Обновляем содержимое всплывающего окна
      const popupContent = `<strong>${currentVehicle?.name || 'Транспорт'}</strong><br>
                           Дата: ${formatDate(point.timestamp)}<br>
                           Время: ${formatTime(point.timestamp)}<br>
                           Скорость: ${point.speed} км/ч`;
      
      animatedMarkerRef.current.setPopupContent(popupContent);
      
      // Сохраняем текущий индекс
      animatedMarkerRef.current._currentPointIndex = index;
    } catch (error) {
      console.error('TrackReport: Ошибка при обновлении анимированного маркера:', error);
    }
  }, [animatedMarkerRef, formatDate, formatTime]);

  // Функция для очистки выделения сегмента трека (копия из TrackMap)
  const clearHighlightedSegment = useCallback(() => {
    try {
      if (mapInstanceRef.current && highlightedPathRef.current) {
        mapInstanceRef.current.removeLayer(highlightedPathRef.current);
        highlightedPathRef.current = null;
        console.log('TrackReport: Выделение сегмента трека очищено');
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при очистке выделения сегмента трека:', error);
    }
  }, [mapInstanceRef, highlightedPathRef]);

  // Заглушка для updateTimeSlider (может быть реализована отдельно) - временно отключена
  // const updateTimeSlider = useCallback((index) => {
  //   // TODO: Реализовать обновление временного слайдера
  //   console.log('TrackReport: updateTimeSlider вызвана с индексом:', index);
  // }, []);

  return {
    updateMapWithTrackDataInternal,
    updateMapView,
    refreshMapView,
    updateAnimatedMarker,
    clearHighlightedSegment
  };
}; 